import { Modal } from 'components/common';
import { LifeCyclePlaybookListBoxSelector } from 'components/dashboard';
import { ReactComponent as PlaybookActiveIcon } from 'components/icons/icon-playbook-active.svg';
import { ReactComponent as PlaybookAtRiskIcon } from 'components/icons/icon-playbook-at-risk.svg';
import { ReactComponent as PlaybookChurnedIcon } from 'components/icons/icon-playbook-churned.svg';
import { ButtonCTASmall, SuggestionCallout, Tooltip } from 'components/ui';
import { PAID_PLAN_TOOLTIP } from 'config.js';
import querystring from 'querystring';
import { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useAppSelector } from 'store';
import { useGetDashboardLifecycleQuery } from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import { useParseURLQueryParams } from 'utils';
import { useStoreSubscriptionPlan } from 'utils/useStoreSubscriptionPlan';

export default function LifecyclePlaybook() {
  const { storeSubscriptionPlan, subscriptionPlans } =
    useStoreSubscriptionPlan();

  const { query } = useParseURLQueryParams();

  const { currentStore } = useAppSelector(userDetailsState);

  const history = useHistory();

  const locale = currentStore.completeInfo?.primary_locale ?? 'en';

  const { data } = useGetDashboardLifecycleQuery(
    { serializedStoreName: currentStore.serializedStoreName },
    {
      skip: !!!currentStore.serializedStoreName,
    }
  );
  const segments = data?.segments;

  const segmentsName = ['Active', 'At risk', 'Churned'];
  const segmentsCategory = ['New signups', 'One timers', 'Repeats', 'Loyals'];

  const segmentCustomColorAndIcon = {
    active: { color: 'text-segments-active', icon: <PlaybookActiveIcon /> },
    at_risk: { color: 'text-segments-at-risk', icon: <PlaybookAtRiskIcon /> },
    churned: {
      color: 'text-segments-churned-title',
      icon: <PlaybookChurnedIcon />,
    },
  };

  const translatedSegmentsName = {
    at_risk_new_signups: {
      name: 'At risk',
      category: 'New signups',
      description:
        "New signups who haven't purchased and are at risk of churning.",
    },
    at_risk_one_timers: {
      name: 'At risk',
      category: 'One timers',
      description: 'Customers with 1 purchase and who are at risk of churning.',
    },
    at_risk_repeats: {
      name: 'At risk',
      category: 'Repeats',
      description:
        'Customers with 2 purchases and who are at risk of churning.',
    },
    at_risk_loyals: {
      name: 'At risk',
      category: 'Loyals',
      description:
        'Customers with 3+ purchases and who are at risk of churning.',
    },
    active_new_signups: {
      name: 'Active',
      category: 'New signups',
      description:
        "New signups who haven't purchased and are still likely to convert.",
    },
    active_one_timers: {
      name: 'Active',
      category: 'One timers',
      description:
        'Customers with 1 purchase and who are in the period most likely to convert again.',
    },
    active_repeats: {
      name: 'Active',
      category: 'Repeats',
      description:
        'Customers with 2 purchases and who are in the period most likely to convert again.',
    },
    active_loyals: {
      name: 'Active',
      category: 'Loyals',
      description:
        'Customers with 3+ purchases and who are in the period most likely to convert again.',
    },
    churned_signups: {
      name: 'Churned',
      category: 'New signups',
      description: "Signups who haven't purchased and have churned.",
    },
    churned_one_timers: {
      name: 'Churned',
      category: 'One timers',
      description: 'Customers with 1 purchase and who have churned.',
    },
    churned_repeats: {
      name: 'Churned',
      category: 'Repeats',
      description: 'Customers with 2 purchases and who have churned.',
    },
    churned_loyals: {
      name: 'Churned',
      category: 'Loyals',
      description: 'Customers with 3+ purchases and who have churned.',
    },
  };

  const [segment, setSegment] = useState(
    (query?.segment_name &&
      translatedSegmentsName[query?.segment_name]?.name) ??
      segmentsName[0]
  );
  const [category, setCategory] = useState(
    (query?.segment_name &&
      translatedSegmentsName[query?.segment_name]?.category) ??
      segmentsCategory[0]
  );

  const playbookData = {
    segmentSize:
      query?.segment_name &&
      segments &&
      segments[query.segment_name]?.segment_size,
    adlo: query?.segment_name && segments && segments[query.segment_name]?.adlo,
    ano: query?.segment_name && segments && segments[query.segment_name]?.ano,
    aov: query?.segment_name && segments && segments[query.segment_name]?.aov,
    aclv: query?.segment_name && segments && segments[query.segment_name]?.aclv,
    action:
      query?.segment_name && segments && segments[query.segment_name]?.action,
    discount:
      query?.segment_name && segments && segments[query.segment_name]?.discount,
  };

  /**
   * Update URL Query according to Selector and to keep background always at the Dashboard
   * @param {querystring.ParsedUrlQueryInput | undefined} queryParams
   */
  const updateUrlQueryParams = (queryParams) => {
    history.replace({
      search: querystring.stringify({
        ...query,
        ...queryParams,
      }),
      state: { background: { ...history.location, pathname: '/dashboard' } },
    });
  };

  /**
   * Handle Selectors Change method
   * @param {string} selected
   */
  const handleOnChange = (selected) => {
    // Check first if the Segment Name selector was the one changed
    if (segmentsName.includes(selected)) {
      setSegment(selected);

      if (
        selected.toLowerCase() === 'churned' &&
        category.replace(/\s/g, '_').toLowerCase() === 'new_signups'
      ) {
        updateUrlQueryParams({
          segment_name: selected.toLowerCase() + '_signups',
        });
      } else {
        updateUrlQueryParams({
          segment_name:
            selected.replace(/\s/g, '_').toLowerCase() +
            '_' +
            category.replace(/\s/g, '_').toLowerCase(),
        });
      }
    }

    // Check if the Segments Category was the one changed
    if (segmentsCategory.includes(selected)) {
      setCategory(selected);

      // Check if
      if (
        segment.toLowerCase() === 'churned' &&
        selected.replace(/\s/g, '_').toLowerCase() === 'new_signups'
      ) {
        updateUrlQueryParams({
          segment_name: segment.replace(/\s/g, '_').toLowerCase() + '_signups',
        });
      } else {
        updateUrlQueryParams({
          segment_name:
            segment.replace(/\s/g, '_').toLowerCase() +
            '_' +
            selected.replace(/\s/g, '_').toLowerCase(),
        });
      }
    }
  };

  const handleOnClose = () => {
    history.push({
      pathname: '/dashboard',
      search: querystring.stringify({
        store_name: query.store_name,
      }),
    });
  };

  return (
    <Modal isOpen={true}>
      <div
        className="absolute inset-0 bg-transparent"
        onClick={handleOnClose}
      />
      <main className="z-auto w-screen pb-5 bg-white border border-solid rounded-2xl lg:w-segments-736px transform-gpu md:scale-90 2xl:scale-100">
        <div className="w-full px-5 bg-segments-accent-color-1 bg-opacity-5 pt-7 pb-9 lg:px-12">
          <header className="flex items-center justify-between">
            <p className="leading-4 text-black opacity-50 text-segments-13px">
              Lifecycle journey <span className="font-bold">Playbook</span>
            </p>
            <button className="focus:outline-none" onClick={handleOnClose}>
              <svg
                className="w-6 h-6 fill-current text-segments-dark"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </header>
          <section>
            <div className="grid-cols-3 pt-6 lg:grid ">
              <div className="col-span-2">
                <div className="flex items-start space-x-5">
                  <LifeCyclePlaybookListBoxSelector
                    selected={segment}
                    // @ts-ignore
                    setSelected={handleOnChange}
                    data={segmentsName}
                    selectedColor={
                      // @ts-ignore
                      segmentCustomColorAndIcon[
                        segment.replace(/\s/g, '_').toLowerCase()
                      ].color
                    }
                  />
                  <LifeCyclePlaybookListBoxSelector
                    selected={category}
                    // @ts-ignore
                    setSelected={handleOnChange}
                    data={segmentsCategory}
                  />
                </div>
                <div className="pt-1.5">
                  <p className="text-base text-black text-opacity-50">
                    {translatedSegmentsName[query?.segment_name]?.description}
                  </p>
                </div>
              </div>
              <div className="hidden lg:block justify-self-end">
                {
                  // @ts-ignore
                  segmentCustomColorAndIcon[
                    segment.replace(/\s/g, '_').toLowerCase()
                  ].icon
                }
              </div>
            </div>
          </section>
        </div>
        <div className="px-5 py-3 lg:px-12">
          <div className="text-base divide-y ">
            <div className="flex py-3">
              <h4 className="w-56">Users</h4>
              <h4>{playbookData.segmentSize?.toLocaleString(locale)}</h4>
            </div>
            <div className="flex py-3">
              <h4 className="w-56">Avg. days since order</h4>
              <h4>{playbookData.adlo?.toLocaleString(locale)}</h4>
            </div>
            <div className="flex py-3">
              <h4 className="w-56">Avg. frequency</h4>
              <h4>{playbookData.ano?.toLocaleString(locale)}</h4>
            </div>
            <div className="flex py-3">
              <h4 className="w-56">Avg. order value</h4>
              <h4>
                {playbookData.aov?.toLocaleString(locale, {
                  style: 'currency',
                  currency: currentStore.completeInfo?.currency ?? 'USD',
                })}
              </h4>
            </div>
            <div className="flex py-3">
              <h4 className="w-56">Avg. CLV</h4>
              <h4>
                {playbookData.aclv?.toLocaleString(locale, {
                  style: 'currency',
                  currency: currentStore.completeInfo?.currency ?? 'USD',
                })}
              </h4>
            </div>
          </div>
          <div className="pt-2">
            <SuggestionCallout>
              <div className="px-6 pt-4 pb-7 ">
                <h3 className="text-base text-black text-opacity-50">
                  Suggested action
                </h3>
                <p className="font-bold pt-1.5">{playbookData.action}</p>
                <hr className="my-3" />
                <h3 className="text-base text-black text-opacity-50">
                  Suggested discount
                </h3>
                <p className="font-bold pt-1.5">{playbookData.discount}</p>
              </div>
            </SuggestionCallout>
          </div>
          <div className="flex justify-between mt-6">
            <div>
              <ButtonCTASmall
                type="button"
                variant="secondary"
                onClick={handleOnClose}
              >
                <p className="px-4 font-normal">Close</p>
              </ButtonCTASmall>
            </div>
            <div>
              <Tooltip
                text={PAID_PLAN_TOOLTIP}
                disabled={
                  storeSubscriptionPlan > subscriptionPlans.small_business
                }
              >
                <Link
                  to={{
                    pathname: `/customers/segments/integrations`,
                    search: `?${querystring.stringify({
                      ...query,
                      segment_name: query.segment_name?.replaceAll('_', ' '),
                    })}`,
                    state: {
                      background: {
                        ...history.location,
                        pathname: '/dashboard',
                      },
                    },
                  }}
                  className={`flex w-full px-5 py-1.5 text-base font-bold text-white border focus:outline-none border-segments-accent-color-1 rounded-xl bg-segments-accent-color-1 hover:bg-segments-dark ${
                    storeSubscriptionPlan < subscriptionPlans.small_business &&
                    'pointer-events-none opacity-50'
                  }`}
                >
                  Sync
                </Link>
              </Tooltip>
            </div>
          </div>
        </div>
      </main>
    </Modal>
  );
}
